export const deviceMotionBodyToCameraFrameTransform: number[][] = [
  [0, -1, 0],
  [-1, 0, 0],
  [0, 0, -1],
];

export const minRotationFrames = 18;

export const pipelineStatusColors: Record<string, string> = {
  PROCESSING: "rgba(242, 189, 53, 1)",
  FAILURE: "var(--sm-error, rgba(255, 98, 126, 1))",
  SUCCESS: "var(--sm-success, rgba(129, 192, 131, 1))",
  APPROVED: "var(--sm-text-muted, #8A8A98)",
};
export const capacity = 3; // Window size for the moving average

export const leftBodyPartsOfInterest = [
  "left_shoulder",
  "left_hip",
  "left_elbow",
];

export const rightBodyPartsOfInterest = [
  "right_shoulder",
  "right_hip",
  "right_elbow",
];

export const forwardStateThreshold = -0.85;
export const backStateThreshold = 0.85;

// Buffer of gravity samples used by useDeviceMotion (5 Hz)
export const bufferLimit = 20;
export const GRAVITY_CONSTANT = 9.81;

// -------------------- isStationary tuning constants --------------------
// Thresholds expressed in g (or g^2) and degrees, device-agnostic
export const STATIONARY_MAG_DRIFT_THRESH_G = 0.09; // |mean(|g|) - 1| > 0.09 ⇒ movement
export const STATIONARY_DELTA_RMS_THRESH_G = 0.4; // RMS of successive deltas > 0.4 g ⇒ movement
export const STATIONARY_TILT_DRIFT_DEG = 3.0; // long-window orientation drift > 3.0° ⇒ tilt/slide
export const STATIONARY_ANGLE_JITTER_DEG = 4.0; // short-window orientation jitter > 4.0° ⇒ handheld
export const STATIONARY_SUM_VAR_SHORT_THRESH_G2 = 0.06; // sum of axis variances (g^2)
export const STATIONARY_SHORT_WINDOW_SAMPLES = 10; // ≈2s at 5 Hz
export const STATIONARY_LONG_WINDOW_SAMPLES = 20; // ≈4s at 5 Hz (capped by buffer)

export const maxJointDistThreshold: number = 1.3;
export const minJointDistThreshold: number = -1.1;
export const midRotationThreshold: number = 0.25;

export const backgroundProcessingEndpoint = process.env
  .REACT_APP_SEGMENTATION_ENDPOINT as string;

export const uploadPhotosEndpoint = process.env
  .REACT_APP_UPLOAD_PHOTOS_ENDPOINT as string;

export const isProdEnv = process.env.REACT_APP_IS_PROD_ENV === "true";

// -------------------- scan liveness heartbeat --------------------
// Written by ScanUploader while it owns a scan, read by Utils/scanRecovery to
// tell "a page is still working on this scan" apart from "the page that owned
// it is gone". They live here rather than in either module because
// scanRecovery already imports ScanUploader — defining them in scanRecovery
// would make that import cycle back on itself.
export const SCAN_HEARTBEAT_KEY = "scanHeartbeat";
export const SCAN_HEARTBEAT_INTERVAL_MS = 2_000;
// 5x the write interval: generous enough to ride out timer jitter and a brief
// background throttle, short enough that a user returning to an evicted tab
// waits seconds rather than minutes.
export const SCAN_HEARTBEAT_STALE_MS = 10_000;

// Rotation error codes and descriptions
export const rotationErrorDescriptions = {
  "over-rotation": {
    title: "Over rotation",
    description:
      "Complete one full spin. Stop and stay stationary once you are facing the camera again.",
  },
  "under-rotation": {
    title: "Incomplete rotation",
    description: "Complete one full spin until you face the camera again.",
  },
  "rotation-too-quick": {
    title: "Rotated too quickly",
    description: "Complete one full spin during the scan countdown.",
  },
  "did-not-rotate-in-place": {
    title: "Did not rotate in place",
    description:
      "Stay in place while rotating. Do not move closer or further from the camera. Try taking smaller steps.",
  },
  "device-moved": {
    title: "Device moved",
    description: "Keep your device stationary during the scan capture.",
  },
  "rotated-before-start": {
    title: "Rotated too early",
    description: "Face forward and stand in T-pose until scan capture begins.",
  },
  "not-in-tpose": {
    title: "Stay in T-Pose",
    description: "Keep your arms up in T-pose as you spin 1x in-place.",
  },
  "body-not-detected": {
    title: "Body not detected",
    description:
      "Make sure the top of your head and bottom of your feet are fully in view of the camera. Stand up straight.",
  },
  "insufficient-rotation-data": {
    title: "Incomplete data capture",
    description: "Uh oh! Something went wrong. Please try again.",
  },
  "upload-failed": {
    title: "Upload failed",
    description: "Uh oh! Something went wrong. Please try again.",
  },
  "camera-frozen": {
    title: "Camera stopped",
    description:
      "Your camera stopped updating. Close this scan with the X and start again.",
  },
};
